import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn, isPuzzleId, type PuzzleId } from '../luna-common';
import '../luna-board-art/luna-board-art';
import '../luna-icon/luna-icon';

// The decluttered home card from the Figma "Game Card — Simple" component: a
// big square board thumbnail plus ONE "Play <Name>" action — no title,
// description, or secondary action. The whole card is a single <button>: the
// hover/focus highlight (accent ring + lifted shadow) makes the tile read as
// one hit target, so the whole card is clickable. luna-game-card stays for
// contexts that want the fuller description (e.g. the programme page).
@customElement('luna-game-card-simple')
export class LunaGameCardSimple extends LitPage {
  private _puzzle: PuzzleId = 'map';

  @property({ attribute: 'puzzle' })
  get puzzle(): PuzzleId {
    return this._puzzle;
  }

  set puzzle(value: string | null) {
    const next = String(value ?? '');
    if (!isPuzzleId(next)) {
      devWarn(`luna-game-card-simple: unknown puzzle "${next}" — falling back to "map"`);
      this._puzzle = 'map';
      return;
    }
    this._puzzle = next;
  }

  @property({ attribute: 'name' }) name = '';

  private emitPlay(): void {
    this.dispatchEvent(
      new CustomEvent('luna-play', {
        detail: { puzzle: this._puzzle },
        bubbles: true,
        composed: true,
      })
    );
  }

  content() {
    return html`
      <button
        type="button"
        class="luna-game-card-simple luna-game-card-simple--${this._puzzle}"
        @click=${this.emitPlay}
      >
        <span class="luna-game-card-simple__art luna-game-card-simple__art--${this._puzzle}">
          <luna-board-art puzzle=${this._puzzle} size="184"></luna-board-art>
        </span>
        <span class="luna-game-card-simple__play">
          <luna-icon name="play" size="20" aria-hidden="true"></luna-icon>
          <span class="luna-game-card-simple__play-label mbw-type-label-button-xl"
            >Play ${this.name}</span
          >
        </span>
      </button>
    `;
  }

  styles() {
    return css`
      .luna-game-card-simple {
        display: flex;
        flex-direction: column;
        gap: 16px; /* pinned: figma Game Card — Simple */
        box-sizing: border-box;
        width: 100%;
        padding: 16px; /* pinned: figma Game Card — Simple */
        border: none;
        border-radius: var(--luna-radius-card);
        background: var(--mbw-surface-card);
        cursor: pointer;
        box-shadow:
          inset 0 0 0 1px var(--mbw-card-hairline),
          0 4px 0 var(--mbw-card-ledge),
          0 10px 22px rgba(20, 40, 90, 0.1); /* pinned: figma luna/card-lift-blue */
        transition: box-shadow var(--mbw-motion-dur-fast);
      }

      .luna-game-card-simple__art {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 220px; /* pinned: figma Game Card — Simple */
        border-radius: var(--luna-radius-board);
      }

      .luna-game-card-simple__art--map {
        background: var(--mbw-palette-map-tint);
      }

      .luna-game-card-simple__art--pegs {
        background: var(--mbw-palette-pegs-tint);
      }

      .luna-game-card-simple__art--rectangles {
        background: var(--mbw-palette-rectangles-tint);
      }

      .luna-game-card-simple__art--tents {
        background: var(--mbw-palette-tents-tint);
      }

      .luna-game-card-simple__art--unruly {
        background: var(--mbw-palette-unruly-tint);
      }

      .luna-game-card-simple__art--untangle {
        background: var(--mbw-palette-untangle-tint);
      }

      .luna-game-card-simple__play {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px; /* pinned: figma Game Card — Simple */
        width: 100%;
        height: 64px; /* pinned: figma Game Card — Simple */
        box-sizing: border-box;
        border-radius: var(--luna-radius-btn);
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.5),
          0 3px 0 rgba(0, 0, 0, 0.22); /* pinned: figma luna/play-ledge */
      }

      /* Pressing anywhere on the card shrinks the play ledge. */
      .luna-game-card-simple:active .luna-game-card-simple__play {
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.5),
          0 1px 0 rgba(0, 0, 0, 0.22);
      }

      /* Keyboard focus = the design's accent ring (same as hover). */
      .luna-game-card-simple:focus-visible {
        outline: none;
      }

      .luna-game-card-simple__play-label {
        white-space: nowrap; /* pinned: figma Game Card — Simple */
      }

      /* Per-puzzle Play gradient + label colour — same stops as luna-game-card. */
      .luna-game-card-simple--map .luna-game-card-simple__play {
        background-image: linear-gradient(180deg, #33835b 0%, #177245 25%, #15693f 100%); /* pinned: figma Game Card — Simple */
        color: var(--mbw-palette-map-btn-text);
      }

      .luna-game-card-simple--pegs .luna-game-card-simple__play {
        background-image: linear-gradient(180deg, #ce603e 0%, #c74a24 25%, #b74421 100%); /* pinned: figma Game Card — Simple */
        color: var(--mbw-palette-pegs-btn-text);
      }

      .luna-game-card-simple--rectangles .luna-game-card-simple__play {
        background-image: linear-gradient(180deg, #1f79bc 0%, #0067b3 25%, #005fa5 100%); /* pinned: figma Game Card — Simple */
        color: var(--mbw-palette-rectangles-btn-text);
      }

      .luna-game-card-simple--tents .luna-game-card-simple__play {
        background-image: linear-gradient(180deg, #b98e1f 0%, #b07f00 25%, #a27500 100%); /* pinned: figma Game Card — Simple */
        color: var(--mbw-palette-tents-btn-text);
      }

      .luna-game-card-simple--unruly .luna-game-card-simple__play {
        background-image: linear-gradient(180deg, #a76198 0%, #9b4c8a 25%, #8f467f 100%); /* pinned: figma Game Card — Simple */
        color: var(--mbw-palette-unruly-btn-text);
      }

      .luna-game-card-simple--untangle .luna-game-card-simple__play {
        background-image: linear-gradient(180deg, #4f94d9 0%, #3785d4 25%, #337ac3 100%); /* pinned: figma Game Card — Simple */
        color: var(--mbw-palette-untangle-btn-text);
      }

      /* Hover/focus: 3px accent ring + lifted shadow; the hairline inset drops. */
      .luna-game-card-simple--map:hover,
      .luna-game-card-simple--map:focus-visible {
        box-shadow:
          0 0 0 3px var(--mbw-palette-map-accent),
          0 5px 0 var(--mbw-card-ledge),
          0 10px 26px color-mix(in srgb, var(--mbw-palette-map-accent) 38%, transparent); /* pinned: figma Game Card — Simple Hover */
      }

      .luna-game-card-simple--pegs:hover,
      .luna-game-card-simple--pegs:focus-visible {
        box-shadow:
          0 0 0 3px var(--mbw-palette-pegs-accent),
          0 5px 0 var(--mbw-card-ledge),
          0 10px 26px color-mix(in srgb, var(--mbw-palette-pegs-accent) 38%, transparent); /* pinned: figma Game Card — Simple Hover */
      }

      .luna-game-card-simple--rectangles:hover,
      .luna-game-card-simple--rectangles:focus-visible {
        box-shadow:
          0 0 0 3px var(--mbw-palette-rectangles-accent),
          0 5px 0 var(--mbw-card-ledge),
          0 10px 26px color-mix(in srgb, var(--mbw-palette-rectangles-accent) 38%, transparent); /* pinned: figma Game Card — Simple Hover */
      }

      .luna-game-card-simple--tents:hover,
      .luna-game-card-simple--tents:focus-visible {
        box-shadow:
          0 0 0 3px var(--mbw-palette-tents-accent),
          0 5px 0 var(--mbw-card-ledge),
          0 10px 26px color-mix(in srgb, var(--mbw-palette-tents-accent) 38%, transparent); /* pinned: figma Game Card — Simple Hover */
      }

      .luna-game-card-simple--unruly:hover,
      .luna-game-card-simple--unruly:focus-visible {
        box-shadow:
          0 0 0 3px var(--mbw-palette-unruly-accent),
          0 5px 0 var(--mbw-card-ledge),
          0 10px 26px color-mix(in srgb, var(--mbw-palette-unruly-accent) 38%, transparent); /* pinned: figma Game Card — Simple Hover */
      }

      .luna-game-card-simple--untangle:hover,
      .luna-game-card-simple--untangle:focus-visible {
        box-shadow:
          0 0 0 3px var(--mbw-palette-untangle-accent),
          0 5px 0 var(--mbw-card-ledge),
          0 10px 26px color-mix(in srgb, var(--mbw-palette-untangle-accent) 38%, transparent); /* pinned: figma Game Card — Simple Hover */
      }
    `;
  }
}
